import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
    Button,
    Tab,
    Tabs,
    Theme,
    Switch,
    FormControlLabel,
    Tooltip,
    IconButton,
} from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import InfoIcon from '@material-ui/icons/Info'

import { Container, NumberInput } from '../../../../components'
import {
    AreaConfig,
    ListVendor,
    ServiceContract,
    UnitConfig,
    getScCompoundKeyString,
    ServiceContractCompoundKey,
    ApartmentVendor,
} from '../../../../models'
import { useServiceContract } from '../../../../hooks/useServiceContract'
import { VENDOR_MODAL_H, VENDOR_MODAL_W } from './contants'
import { UpdateVendorRequest, VendorAndContractThunk } from '../../../../store'
import { typographyStyle } from '../../../../theme/typographyStyle'
import { UnsavedPricingChangesDialog } from '../../VendorPricing/UnsavedPricingChangesDialog'

interface Props {
    vendor: ListVendor
    theme: Theme
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    apartmentVendorMap: Record<number, ApartmentVendor>
    onSubmit: (inputState: InputState) => void | Promise<unknown>
    updateMaxPricing: (useMaxPricing: boolean) => void
    onClose?: () => void
    setPricingCloseGuard?: (guard: (() => boolean) | null) => void
}

export const StepVendorPrices = (props: Props) => {
    const {
        vendor,
        unitConfigs,
        areaConfigs,
        theme,
        onSubmit,
        apartmentVendorMap,
        updateMaxPricing,
        onClose,
        setPricingCloseGuard,
    } = props

    const [selectedTab, setSelectedTab] = useState(0)
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const { getServiceContractList } = useServiceContract()

    const [scMap, setScMap] = useState<InputState>({})
    const [initialScMap, setInitialScMap] = useState<InputState>({})

    useEffect(() => {
        getServiceContractList({ params: { vendor: vendor.id } }).then(
            (res) => {
                const selectedService = vendor.services[0]
                if (selectedService !== undefined) {
                    mapServiceContracts(res.data)
                }
            },
        )
    }, [])

    const mapServiceContracts = (serviceContracts: ServiceContract[]) => {
        const mapping: InputState = {}

        serviceContracts.forEach((sc) => {
            mapping[getScCompoundKeyString(sc)] = sc.price
        })
        setScMap(mapping)
        setInitialScMap(mapping)
    }

    const isDirty = useMemo(
        () => JSON.stringify(scMap) !== JSON.stringify(initialScMap),
        [scMap, initialScMap],
    )
    const isDirtyRef = useRef(isDirty)
    isDirtyRef.current = isDirty

    useEffect(() => {
        if (!setPricingCloseGuard) {
            return
        }
        setPricingCloseGuard(() => {
            if (isDirtyRef.current) {
                setConfirmCloseOpen(true)
                return true
            }
            return false
        })
        return () => setPricingCloseGuard(null)
    }, [setPricingCloseGuard])

    const saveChanges = async () => {
        setIsSaving(true)
        try {
            await onSubmit(scMap)
            setInitialScMap(scMap)
            setConfirmCloseOpen(false)
        } finally {
            setIsSaving(false)
        }
    }

    const updateInputValue = (
        key: ServiceContractCompoundKey,
        value: number,
    ) => {
        setScMap((prev) => {
            return { ...prev, [getScCompoundKeyString(key)]: value }
        })
    }

    const getServiceLabel = (serviceId: number) => {
        const denominator = unitConfigs.length + areaConfigs.length

        if (denominator === 0) {
            return (
                <span
                    style={{
                        ...typographyStyle(theme.typography.caption),
                        fontWeight: 300,
                        marginRight: theme.spacing(1),
                    }}
                >
                    (n/a)
                </span>
            )
        }

        let numerator = 0
        areaConfigs.forEach((ac) => {
            const k = getScCompoundKeyString({
                serviceId: serviceId,
                areaConfigId: ac.id,
                unitConfigId: null,
                vendorId: vendor.id,
            })

            const value = scMap[k]
            if (value !== undefined && value !== null && value > 0) {
                numerator += 1
            }
        })

        unitConfigs.forEach((uc) => {
            const k = getScCompoundKeyString({
                serviceId: serviceId,
                areaConfigId: null,
                unitConfigId: uc.id,
                vendorId: vendor.id,
            })

            const value = scMap[k]
            if (value !== undefined && value !== null && value > 0) {
                numerator += 1
            }
        })

        if (numerator === denominator) {
            return (
                <CheckCircleOutlineIcon
                    color="primary"
                    fontSize="small"
                    style={{ marginRight: theme.spacing(1) }}
                />
            )
        }

        return (
            <span
                style={{
                    ...typographyStyle(theme.typography.caption),
                    fontWeight: 300,
                    marginRight: theme.spacing(1),
                }}
            >
                ({numerator}/{denominator})
            </span>
        )
    }

    const selectedService = vendor.services[selectedTab]
    if (selectedService === undefined) {
        return null
    }

    return (
        <>
            <Container
                style={{
                    flex: 1,
                    borderTop: `1px solid ${theme.palette.grey[400]}`,
                    maxHeight: VENDOR_MODAL_H,
                    minHeight: VENDOR_MODAL_H,
                    minWidth: VENDOR_MODAL_W,
                    maxWidth: VENDOR_MODAL_W,
                }}
            >
                {/* Left Column, Choose service */}
                <Container
                    style={{
                        flexDirection: 'column',
                        width: 150,
                        borderRight: `1px solid ${theme.palette.grey[400]}`,
                    }}
                >
                    <Tabs
                        value={selectedTab}
                        orientation="vertical"
                        variant="scrollable"
                        onChange={(_, value) => {
                            setSelectedTab(value)
                        }}
                    >
                        {vendor.services.map((srv, index) => {
                            return (
                                <Tab
                                    key={`SERVICE-OFFERING-${srv.id}`}
                                    value={index}
                                    label={
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            <span>{srv.name}</span>
                                            <div style={{ flex: 1 }} />
                                            {getServiceLabel(srv.id)}
                                        </Container>
                                    }
                                    style={{ textTransform: 'none' }}
                                />
                            )
                        })}
                    </Tabs>
                </Container>

                {/* Right Column, Body */}
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        maxHeight: VENDOR_MODAL_H - FOOTER_H,
                    }}
                >
                    {/* Body, Service Contract Columns */}
                    <Container
                        style={{
                            flex: 1,
                            maxHeight: VENDOR_MODAL_H - FOOTER_H,
                            minHeight: VENDOR_MODAL_H - FOOTER_H,
                        }}
                    >
                        {/* Column 1,  Areas*/}
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                                padding: theme.spacing(1),
                                overflowY: 'scroll',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    color: theme.palette.grey[800],
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                Areas (Partial Pricing)
                            </span>

                            {areaConfigs.map((areaConfig) => {
                                return (
                                    <NumberInput
                                        key={`AREA-CONFIG-${areaConfig.id}-${selectedTab}`}
                                        prefix={'$'}
                                        variant="outlined"
                                        label={areaConfig.name}
                                        value={
                                            scMap[
                                                getScCompoundKeyString({
                                                    serviceId:
                                                        selectedService.id,
                                                    vendorId: vendor.id,
                                                    areaConfigId: areaConfig.id,
                                                    unitConfigId: null,
                                                })
                                            ]
                                        }
                                        style={{
                                            marginBottom: theme.spacing(2),
                                        }}
                                        onChange={(e) =>
                                            updateInputValue(
                                                {
                                                    serviceId:
                                                        selectedService.id,
                                                    vendorId: vendor.id,
                                                    areaConfigId: areaConfig.id,
                                                    unitConfigId: null,
                                                },
                                                Number(e.target.value),
                                            )
                                        }
                                    />
                                )
                            })}
                        </Container>

                        {/* Column 2, Unit Types */}
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                                padding: theme.spacing(1),
                                overflowY: 'scroll',
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    color: theme.palette.grey[800],
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                Unit Types (Whole Unit Pricing)
                            </span>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            apartmentVendorMap[vendor.id]
                                                ?.use_max_price || false
                                        }
                                        onChange={(e) =>
                                            updateMaxPricing(e.target.checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label={
                                    <Container style={{ alignItems: 'center' }}>
                                        <span>Price Cap</span>
                                        <Tooltip title="When turned on the individual area pricing will not exceed Fully vacant Unit type price.">
                                            <IconButton
                                                size="small"
                                                style={{
                                                    padding: 4,
                                                    marginLeft: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                <InfoIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                }
                                style={{
                                    marginBottom: theme.spacing(2),
                                    marginLeft: 0,
                                }}
                            />

                            {unitConfigs.map((unitConfig) => {
                                return (
                                    <NumberInput
                                        key={`UNIT-CONFIG-${unitConfig.id}-${selectedTab}`}
                                        prefix={'$'}
                                        variant="outlined"
                                        label={unitConfig.name}
                                        value={
                                            scMap[
                                                getScCompoundKeyString({
                                                    serviceId:
                                                        selectedService.id,
                                                    vendorId: vendor.id,
                                                    areaConfigId: null,
                                                    unitConfigId: unitConfig.id,
                                                })
                                            ]
                                        }
                                        style={{
                                            marginBottom: theme.spacing(2),
                                        }}
                                        onChange={(e) =>
                                            updateInputValue(
                                                {
                                                    serviceId:
                                                        selectedService.id,
                                                    vendorId: vendor.id,
                                                    areaConfigId: null,
                                                    unitConfigId: unitConfig.id,
                                                },
                                                Number(e.target.value),
                                            )
                                        }
                                    />
                                )
                            })}
                        </Container>
                    </Container>

                    {/* Footer, Submit button */}
                    <Container
                        style={{
                            borderTop: `1px solid ${theme.palette.grey[400]}`,
                            maxHeight: FOOTER_H,
                            minHeight: FOOTER_H,
                            padding: theme.spacing(1),
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ textTransform: 'none', height: 40 }}
                            disabled={isSaving}
                            onClick={() => {
                                saveChanges()
                            }}
                        >
                            Save and Close
                        </Button>
                    </Container>
                </Container>
            </Container>
            <UnsavedPricingChangesDialog
                open={confirmCloseOpen}
                isSaving={isSaving}
                onCancel={() => setConfirmCloseOpen(false)}
                onDiscard={() => {
                    setConfirmCloseOpen(false)
                    onClose?.()
                }}
                onSave={saveChanges}
            />
        </>
    )
}

interface InputState {
    [compoundKey: string]: number | null
}

const FOOTER_H = 80
